/**
 * CR107: Magical UX Layer - Context Provider
 * 
 * Manages magical UX state and coordinates delightful interactions
 */

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface MagicalUXState {
  isBeginnerMode: boolean;
  hasSeenFirstBuild: boolean;
  cursorMode: 'normal' | 'sparkle' | 'wand' | 'glow';
  showHints: boolean;
  completedSteps: string[];
  lastAction: string | null;
}

interface MagicalUXContextValue {
  state: MagicalUXState;
  
  // Actions
  setCursorMode: (mode: MagicalUXState['cursorMode']) => void;
  markStepComplete: (stepId: string) => void;
  triggerCelebration: () => void;
  recordAction: (action: string) => void;
  dismissHints: () => void;
  
  // Getters
  shouldShowHint: (hintId: string) => boolean;
  shouldShowSuggestion: (context: string) => boolean;
}

const MagicalUXContext = createContext<MagicalUXContextValue | null>(null);

interface MagicalUXProviderProps {
  children: ReactNode;
  isBeginnerMode?: boolean;
}

export function MagicalUXProvider({ children, isBeginnerMode = false }: MagicalUXProviderProps) {
  const [state, setState] = useState<MagicalUXState>({
    isBeginnerMode,
    hasSeenFirstBuild: false,
    cursorMode: isBeginnerMode ? 'sparkle' : 'normal',
    showHints: isBeginnerMode,
    completedSteps: [],
    lastAction: null,
  });

  const setCursorMode = useCallback((mode: MagicalUXState['cursorMode']) => {
    setState(prev => ({ ...prev, cursorMode: mode }));
  }, []);

  const markStepComplete = useCallback((stepId: string) => {
    setState(prev => ({
      ...prev,
      completedSteps: [...prev.completedSteps, stepId],
    }));
  }, []);

  const triggerCelebration = useCallback(() => {
    setState(prev => ({ ...prev, hasSeenFirstBuild: true }));
  }, []);

  const recordAction = useCallback((action: string) => {
    setState(prev => ({ ...prev, lastAction: action }));
  }, []);

  const dismissHints = useCallback(() => {
    setState(prev => ({ ...prev, showHints: false }));
  }, []);

  const shouldShowHint = useCallback((hintId: string) => {
    return state.showHints && !state.completedSteps.includes(`hint-${hintId}`);
  }, [state.showHints, state.completedSteps]);

  const shouldShowSuggestion = useCallback((context: string) => {
    return state.isBeginnerMode && state.completedSteps.includes(context);
  }, [state.isBeginnerMode, state.completedSteps]);

  return (
    <MagicalUXContext.Provider value={{
      state,
      setCursorMode,
      markStepComplete,
      triggerCelebration,
      recordAction,
      dismissHints,
      shouldShowHint,
      shouldShowSuggestion,
    }}>
      {children}
    </MagicalUXContext.Provider>
  );
}

export function useMagicalUX() {
  const context = useContext(MagicalUXContext);
  if (!context) {
    throw new Error('useMagicalUX must be used within a MagicalUXProvider');
  }
  return context;
}